import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Container, Box, Button } from '@mui/material'
import { Map as ImmutableMap } from 'immutable'
import { useSelector } from 'react-redux'
import { getAuthPermissions, PERM_DELETE_CONTENT, PERM_UPDATE_CONTENT } from 'services/auth'
import { selectAuth } from 'services/auth/redux/selectors'
import { selectBrooklynServerUrl } from 'services/config/redux/selectors'
import { getIdFromV2Location, removeContent } from 'services/content'
import { selectLocation } from 'services/location/redux/selectors'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ConfirmDialog from 'gui/components/common/ConfirmDialog'
import ContentDetailQueryStates from './ContentDetailQueryStates'
import ContentInformationSection from './ContentInformationSection'
import VideoContentReferencesSection from './VideoContentReferencesSection'
import VideoContentReferencesExtendedSection from './VideoContentReferencesExtendedSection'
import VideoContentReferencesYoreSection from './VideoContentReferencesYoreSection'
import AnonymousFieldsSection from './AnonymousFieldsSection'
import AIMetadataGeneration from './ai-metadata-generation'
import AIMarketingPlanGeneration from './ai-marketing-plan-generation'
import AITranscript from './ai-transcript'
import { useTranscriptFetcher } from './ai-helpers'
import type { AiToolsSharedTranscript } from './ai-helpers'
import { GET_VIDEO_CONTENT, UPDATE_VIDEO_CONTENT } from './queries'
import ContentMediaSection from './content-detail-media/content-media-section'
import ContentImagesSection from './content-detail-images/content-images-section'
import EpisodesSection from './EpisodesSection'
import FloatingSaveCancelButtons from './FloatingSaveCancelButtons'
import ContentDetailSnackbar, { useContentDetailSnackbar } from './ContentDetailSnackbar'
import NewEditorBanner from './LegacyEditorBanner'
import { useSectionManagedSlot } from './useSectionManagedSlot'
import type { ContentInfoChanges } from './types'
import './course-detail.scss'

const SEO_FIELD_NAMES = new Set(['seoTitle', 'seoDescription', 'seoKeywords'])

export interface SeriesDetailV2Props {
  /** When provided, use this id instead of reading from V2 location */
  id?: string | number
}

/**
 * Series Detail V2 Component
 * GraphQL-powered detail page for series content at /v2/content/:id.
 * Same sections as VideoDetailV2 plus Episodes (added separately).
 */
function SeriesDetailV2 (props: SeriesDetailV2Props = {}) {
  const { id: idProp } = props
  const location = useSelector(selectLocation)
  const auth = useSelector(selectAuth)
  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)
  const canDelete = authPermissions.includes(PERM_DELETE_CONTENT)
  const { url: brooklynUrl } = useSelector(selectBrooklynServerUrl)

  const idFromLocation = getIdFromV2Location(location)
  const id = idProp !== undefined && idProp !== null ? idProp : idFromLocation

  const { loading, error, data, refetch } = useQuery(GET_VIDEO_CONTENT, {
    variables: { id },
    skip: !id,
  })

  const seriesData = data?.contentList?.content?.[0] ?? null

  // Parent-managed: section reports changes up, parent owns mutation
  const [contentInfoDirty, setContentInfoDirty] = useState<boolean>(false)
  const [contentInfoChanges, setContentInfoChanges] = useState<ContentInfoChanges | null>(null)
  const [mediaDirty, setMediaDirty] = useState<boolean>(false)
  const [mediaChanges, setMediaChanges] = useState<ContentInfoChanges | null>(null)

  // Section-managed: sections own save; useSectionManagedSlot bundles dirty + saveHandlerRef per section
  const contentRefs = useSectionManagedSlot()
  const contentRefsExtended = useSectionManagedSlot()
  const contentRefsYore = useSectionManagedSlot()
  const anonSlot = useSectionManagedSlot()

  const [resetTrigger, setResetTrigger] = useState<number>(0)
  const [aiInjectedMetadataInfo, setAiInjectedMetadataInfo] = useState<Record<string, unknown>>({})
  const [aiInjectedMetadataSeo, setAiInjectedMetadataSeo] = useState<Record<string, unknown>>({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)

  const isDirty =
    contentInfoDirty ||
    mediaDirty ||
    contentRefs.dirty ||
    contentRefsExtended.dirty ||
    contentRefsYore.dirty ||
    anonSlot.dirty

  const handleContentInfoDirtyChange = useCallback((isContentDirty: boolean) => {
    setContentInfoDirty(isContentDirty)
  }, [])

  const handleContentInfoChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setContentInfoChanges(changes)
  }, [])

  const handleMediaDirtyChange = useCallback((isMediaDirty: boolean) => {
    setMediaDirty(isMediaDirty)
  }, [])

  const handleMediaChangesUpdate = useCallback((changes: ContentInfoChanges | null) => {
    setMediaChanges(changes)
  }, [])

  const [updateVideoContent, { loading: saving }] = useMutation(UPDATE_VIDEO_CONTENT, {
    refetchQueries: [{ query: GET_VIDEO_CONTENT, variables: { id } }],
    awaitRefetchQueries: true,
  })

  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useContentDetailSnackbar()

  const handleSave = async () => {
    try {
      if (
        !contentInfoChanges &&
        !mediaChanges &&
        !contentRefs.dirty &&
        !contentRefsExtended.dirty &&
        !contentRefsYore.dirty &&
        !anonSlot.dirty
      ) {
        showSnackbar('No changes to save', 'info')
        return
      }

      let combinedChanges: ContentInfoChanges | null = null
      const parentManagedChanges = [contentInfoChanges, mediaChanges].filter(
        Boolean,
      ) as ContentInfoChanges[]
      if (parentManagedChanges.length > 0) {
        combinedChanges = {
          contentInput: Object.assign({}, ...parentManagedChanges.map((c) => c.contentInput)),
          generatePath: parentManagedChanges.some((c) => c.generatePath),
        }
      }

      if (combinedChanges) {
        await updateVideoContent({
          variables: {
            content: combinedChanges.contentInput,
            generatePath: combinedChanges.generatePath ?? false,
          },
        })
      }

      if (contentRefs.dirty && contentRefs.saveHandlerRef.current) {
        await contentRefs.saveHandlerRef.current()
      }

      if (contentRefsExtended.dirty && contentRefsExtended.saveHandlerRef.current) {
        await contentRefsExtended.saveHandlerRef.current()
      }

      if (contentRefsYore.dirty && contentRefsYore.saveHandlerRef.current) {
        await contentRefsYore.saveHandlerRef.current()
      }

      if (anonSlot.dirty && anonSlot.saveHandlerRef.current) {
        await anonSlot.saveHandlerRef.current()
      }

      await refetch()
      setAiInjectedMetadataInfo({})
      setAiInjectedMetadataSeo({})
      showSnackbar('Content saved successfully!', 'success')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      showSnackbar(`Error saving: ${errorMessage}`, 'error')
    }
  }

  const handleCancel = () => {
    setAiInjectedMetadataInfo({})
    setAiInjectedMetadataSeo({})
    setResetTrigger((prev) => prev + 1)
    showSnackbar('Changes discarded', 'info')
  }

  /** Adapter so AI components (expecting Immutable Map) can read from GraphQL seriesData */
  const contentForAiTools = useMemo(
    () => ImmutableMap((seriesData ?? {}) as Record<string, unknown>),
    [seriesData],
  )

  const {
    fetchTranscript,
    transcript: fetchedTranscript,
    loading: transcriptLoading,
    error: transcriptError,
  } = useTranscriptFetcher()
  const [aiToolsTranscriptDraft, setAiToolsTranscriptDraft] = useState('')

  useEffect(() => {
    if (fetchedTranscript != null && fetchedTranscript !== '') {
      setAiToolsTranscriptDraft(fetchedTranscript)
    }
  }, [fetchedTranscript])

  const aiToolsSharedTranscript: AiToolsSharedTranscript = {
    fetchTranscript,
    fetchedTranscript,
    transcriptLoading,
    transcriptError: transcriptError ?? null,
    transcriptDraft: aiToolsTranscriptDraft,
    onTranscriptDraftChange: setAiToolsTranscriptDraft,
  }

  const setFieldValueFromAi = useCallback((field: string, value: unknown) => {
    if (SEO_FIELD_NAMES.has(field)) {
      setAiInjectedMetadataSeo((prev) => ({ ...prev, [field]: value }))
    } else {
      setAiInjectedMetadataInfo((prev) => ({ ...prev, [field]: value }))
    }
  }, [])

  const openDeleteConfirm = useCallback(() => setDeleteConfirmOpen(true), [])
  const closeDeleteConfirm = useCallback(() => setDeleteConfirmOpen(false), [])

  const handleDeleteConfirm = useCallback(async () => {
    const contentIdForDelete = seriesData?.id ?? id
    if (contentIdForDelete == null) return
    await removeContent({
      auth,
      server: brooklynUrl,
      contentId: String(contentIdForDelete),
    })
    window.location.replace('/content')
  }, [auth, brooklynUrl, seriesData?.id, id])

  return (
    <ContentDetailQueryStates
      loading={loading}
      error={error}
      hasData={!!seriesData}
      errorTitle="Error loading series"
      notFoundTitle="Series not found"
      notFoundDetail={`No series found with ID: ${id}`}
    >
      <Container className="course-detail content-detail" maxWidth={false}>
        {id != null ? <NewEditorBanner contentId={id} /> : null}
        <Container className="content-detail__section content-information" maxWidth={false}>
          <ContentInformationSection
            contentData={seriesData}
            isViewOnly={isViewOnlyMode}
            onDirtyChange={handleContentInfoDirtyChange}
            onChangesUpdate={handleContentInfoChangesUpdate}
            onResetTriggered={resetTrigger}
            aiInjectedMetadata={
              Object.keys(aiInjectedMetadataInfo).length > 0 ? aiInjectedMetadataInfo : undefined
            }
          />
        </Container>

        <AnonymousFieldsSection
          contentData={seriesData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={anonSlot.onDirtyChange}
          saveHandlerRef={anonSlot.saveHandlerRef}
          onResetTriggered={resetTrigger}
          aiInjectedMetadata={
            Object.keys(aiInjectedMetadataSeo).length > 0 ? aiInjectedMetadataSeo : undefined
          }
        />

        <Container className="content-detail__section ai-tools" maxWidth={false}>
          <SectionAccordion title="✨ AI Tools" collapsed={true}>
            <AITranscript
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={seriesData?.featureMediaId}
              sharedTranscript={aiToolsSharedTranscript}
            />
            <Box sx={{ my: 4, borderBottom: '2px solid #e0e0e0' }} />
            <AIMetadataGeneration
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={seriesData?.featureMediaId}
              setFieldValue={setFieldValueFromAi}
              sharedTranscript={aiToolsSharedTranscript}
            />
            <Box sx={{ my: 4, borderBottom: '2px solid #e0e0e0' }} />
            <AIMarketingPlanGeneration
              content={contentForAiTools}
              isViewOnlyMode={isViewOnlyMode}
              featureMediaId={seriesData?.featureMediaId}
              sharedTranscript={aiToolsSharedTranscript}
            />
          </SectionAccordion>
        </Container>

        <VideoContentReferencesSection
          contentData={seriesData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefs.onDirtyChange}
          saveHandlerRef={contentRefs.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesExtendedSection
          contentData={seriesData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsExtended.onDirtyChange}
          saveHandlerRef={contentRefsExtended.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <VideoContentReferencesYoreSection
          contentData={seriesData}
          isViewOnly={isViewOnlyMode}
          onDirtyChange={contentRefsYore.onDirtyChange}
          saveHandlerRef={contentRefsYore.saveHandlerRef}
          onResetTriggered={resetTrigger}
        />

        <Container className="content-detail__section media-information" maxWidth={false}>
          <SectionAccordion title="Media Information" collapsed={false}>
            <ContentMediaSection
              contentData={seriesData}
              isViewOnly={isViewOnlyMode}
              onDirtyChange={handleMediaDirtyChange}
              onChangesUpdate={handleMediaChangesUpdate}
              onResetTriggered={resetTrigger}
            />
          </SectionAccordion>
        </Container>

        {seriesData?.id != null && seriesData?.language != null && (
          <EpisodesSection seriesId={seriesData.id} language={seriesData.language} />
        )}

        <Container className="content-detail__section content-images" maxWidth={false}>
          <SectionAccordion title="Images" collapsed={false}>
            <ContentImagesSection
              contentId={seriesData?.contentId}
              contentType={seriesData?.contentType}
              language={seriesData?.language}
              primaryLanguage={seriesData?.primaryLanguage}
            />
          </SectionAccordion>
        </Container>

        <Container
          className="content-detail__section content-actions-delete-section"
          maxWidth={false}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="error"
              onClick={openDeleteConfirm}
              disabled={isViewOnlyMode || saving || !canDelete || !seriesData}
              sx={{ minWidth: '120px', height: '36px', fontSize: '14px' }}
            >
              Delete
            </Button>
          </Box>
        </Container>

        {!isViewOnlyMode && (
          <FloatingSaveCancelButtons
            visible={isDirty}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        <ContentDetailSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />

        <ConfirmDialog
          open={deleteConfirmOpen}
          onClose={closeDeleteConfirm}
          title="Delete Content"
          description={`Are you sure you want to delete "${(seriesData?.title ?? '').trim() || 'this series'}"?`}
          onConfirm={handleDeleteConfirm}
          confirmLabel="Delete"
          busyLabel="Deleting..."
          severity="error"
        />
      </Container>
    </ContentDetailQueryStates>
  )
}

export default SeriesDetailV2
